import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import { fmtCompact, fmtNumber } from "@/lib/format";
import DeltaBadge from "@/components/DeltaBadge";
import { Smartphone, Monitor, Tablet } from "lucide-react";

const DEVICE_ICON = { Mobile: Smartphone, Desktop: Monitor, Tablet: Tablet };

export default function Website({ website }) {
  if (!website) return null;
  return (
    <section data-testid="website-section">
      <h2 className="mb-4 font-heading text-2xl font-bold tracking-tight">Your Website</h2>
      <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
            <div className="flex items-end justify-between">
              <div>
                <p className="font-heading text-4xl font-bold">{fmtNumber(website.users)}</p>
                <p className="text-sm text-muted-foreground">Website Users</p>
              </div>
              <DeltaBadge value={website.users_delta} />
            </div>
            <div className="mt-4 h-[90px] w-full">
              <ResponsiveContainer width="100%" height={90} minWidth={0}>
                <AreaChart data={website.trend} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="web-trend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--brand-secondary))" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="hsl(var(--brand-secondary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => [fmtCompact(v), "Users"]} labelFormatter={(l) => new Date(l).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} />
                  <Area type="monotone" dataKey="value" stroke="hsl(var(--brand-secondary))" strokeWidth={2} fill="url(#web-trend)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
            <p className="mb-4 text-sm font-semibold">Traffic Sources</p>
            <div className="space-y-3">
              {website.sources.map((s) => (
                <div key={s.source}>
                  <div className="mb-1 flex justify-between text-xs"><span className="font-medium">{s.source}</span><span className="text-muted-foreground">{s.pct}%</span></div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-brand-primary" style={{ width: `${s.pct}%` }} /></div>
                </div>
              ))}
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2 border-t border-border/60 pt-4">
              {website.devices.map((d) => {
                const Icon = DEVICE_ICON[d.device] || Monitor;
                return (
                  <div key={d.device} className="text-center">
                    <Icon className="mx-auto h-4 w-4 text-muted-foreground" />
                    <p className="mt-1 text-lg font-bold">{d.pct}%</p>
                    <p className="text-[11px] text-muted-foreground">{d.device}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
          <p className="mb-4 text-sm font-semibold">Top Landing Pages</p>
          <div className="space-y-3">
            {website.landing_pages.map((lp) => (
              <div key={lp.path} data-testid={`landing-${lp.path}`} className="flex items-center gap-3 rounded-xl border border-border/60 p-2 transition-colors hover:bg-muted/50">
                <img src={lp.thumbnail} alt={lp.title} loading="lazy" className="h-14 w-20 flex-shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-sm">{lp.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{lp.path}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">{fmtCompact(lp.visits)}</p>
                  <p className="text-[11px] text-muted-foreground">visits</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
